import React, { useState, useCallback, useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import "./css/HowDoI.css";
import "./css/Common.css";
import HowDoISearchAPI from "./services/HowDoISearchAPI";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faSms,
  faComments,
  faShareAlt,
  faPhone,
  faUsers,
  faQuestionCircle,
  faTimes,
  faImage,
  faPause,
  faPlay,
  faGraduationCap,
  faBell,
  faFileInvoiceDollar,
  faCreditCard,
  faUserCog,
  faUserPlus,
  faCog,
  faIdCard,
  faSitemap,
  faKey,
  faClipboardList,
  faLink,
  faShieldAlt,
  faLock,
  faShareSquare,
  faMedal,
  faChalkboardTeacher,
  faUndo,
  faChartLine,
  faTag,
  faMobileAlt,
  faHeadset,
  faFilePdf,
  faNewspaper,
  faHistory,
  faTimesCircle,
  faArrowsAltV,
  faThumbtack,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import UserSettingsAPI from "./services/UserSettingsAPI";

function HowDoI({ setHeroContent }) {
  const [fromSetupJourney] = useState(
    () => sessionStorage.getItem("fromSetupJourney") === "true",
  );

  // ─── Pin-as-default state ──────────────────────────────────────────────────
  const [isDefaultPage, setIsDefaultPage] = useState(
    () =>
      (localStorage.getItem("landingPage") || "Dashboard") === "How do I...?",
  );
  const [pinConfirmed, setPinConfirmed] = useState(false);

  const handlePinAsDefault = () => {
    localStorage.setItem("landingPage", "How do I...?");
    UserSettingsAPI.updateSettings({
      defaultLandingPage: "How do I...?",
    }).catch(() => {});
    setIsDefaultPage(true);
    setPinConfirmed(true);
    setTimeout(() => setPinConfirmed(false), 2500);
    window.dispatchEvent(new Event("landingPageChanged"));
  };

  // Re-sync when another page changes the default
  useEffect(() => {
    const sync = () =>
      setIsDefaultPage(
        (localStorage.getItem("landingPage") || "Dashboard") === "How do I...?",
      );
    window.addEventListener("landingPageChanged", sync);
    return () => window.removeEventListener("landingPageChanged", sync);
  }, []);

  // Clear the flag so it doesn't persist on page refresh
  React.useEffect(() => {
    if (fromSetupJourney) {
      sessionStorage.removeItem("fromSetupJourney");
    }
  }, [fromSetupJourney]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearchQuery, setActiveSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [aiMatchedIds, setAiMatchedIds] = useState(null); // null = no search yet
  const [searchError, setSearchError] = useState(false);
  const searchCacheRef = useRef(new Map()); // cache: query → matchedIds
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef(null);
  const inputWrapperRef = useRef(null);

  // --- Suggested search queries (localStorage-backed) ---
  const STORAGE_KEY_SEARCHES = "howdoi_recent_searches";
  const STORAGE_KEY_CLICKS = "howdoi_clicked_cards";
  const MAX_RECENT = 10;

  const defaultSuggestions = [
    { label: "View or change my subscription", type: "default" },
    { label: "Update my payment method", type: "default" },
    { label: "Add users to my account", type: "default" },
  ];

  const getRecentSearches = () => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY_SEARCHES) || "[]");
    } catch {
      return [];
    }
  };

  const saveSearch = (query) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    const recent = getRecentSearches().filter(
      (s) => s.toLowerCase() !== trimmed.toLowerCase(),
    );
    recent.unshift(trimmed);
    localStorage.setItem(
      STORAGE_KEY_SEARCHES,
      JSON.stringify(recent.slice(0, MAX_RECENT)),
    );
  };

  const saveClickedCard = (id) => {
    try {
      const clicks = JSON.parse(
        localStorage.getItem(STORAGE_KEY_CLICKS) || "[]",
      );
      // Move this id to the front (most recent)
      const updated = clicks.filter((c) => c !== id);
      updated.unshift(id);
      localStorage.setItem(
        STORAGE_KEY_CLICKS,
        JSON.stringify(updated.slice(0, MAX_RECENT)),
      );
    } catch {
      /* ignore */
    }
  };

  const getSuggestions = () => {
    const recent = getRecentSearches();
    if (recent.length >= 3) {
      return recent.slice(0, 3).map((s) => ({ label: s, type: "recent" }));
    }
    // Fill remaining slots with defaults (avoiding duplicates)
    const suggestions = recent.map((s) => ({ label: s, type: "recent" }));
    for (
      let i = 0;
      suggestions.length < 3 && i < defaultSuggestions.length;
      i++
    ) {
      const def = defaultSuggestions[i];
      if (
        !suggestions.some(
          (s) => s.label.toLowerCase() === def.label.toLowerCase(),
        )
      ) {
        suggestions.push(def);
      }
    }
    return suggestions.slice(0, 3);
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        inputWrapperRef.current &&
        !inputWrapperRef.current.contains(e.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // List of help questions with their corresponding actions.
  // Note that the subtitle field can be empty or omitted if not needed.
  const helpQuestions = [
    {
      id: 1,
      question: "... check if an <strong>email</strong> is a scam?",
      subtitle:
        "Forward your suspicious email to <strong>security@kinprotect.com</strong> then click here to see the analysis.",
      icon: faEnvelope,
      action: "Forwarded Emails",
      description: "Analyze emails for potential scams and phishing attempts",
    },
    {
      id: 2,
      question: "... check if a <strong>text message</strong> is a scam?",
      subtitle:
        "Forward suspicious text messages to KinShield <strong>+1-888-899-5004</strong> and view your threat analyses here",
      icon: faSms,
      action: "Forwarded Texts",
      description: "Check text SMS messages for suspicious content",
    },
    {
      id: 3,
      question: "... check if a <strong>social media post</strong> is a scam?",
      subtitle: "",
      icon: faShareAlt,
      action: "Social Media Posts",
      description: "Analyze social media posts for fraudulent content",
    },
    {
      id: 4,
      question:
        "... check if a <strong>phone number</strong> is likely associated with scammers?",
      icon: faPhone,
      action: "Phone Number Check",
      description: "Check phone numbers against known scammer databases",
      subtitle: "Check before answering or calling",
    },
    {
      id: 5,
      question:
        "... check if a <strong>screenshot or image</strong> is a scam?",
      subtitle:
        "Upload a screenshot or image to detect tech support scams, fake invoices, and fraudulent content.",
      icon: faImage,
      action: "Screenshot Analysis",
      description:
        "Analyze screenshots and images for scam indicators using AI",
    },
    {
      id: 6,
      question:
        "... evaluate a <strong>business or a person</strong> I am interacting with?",
      icon: faUsers,
      action: "Business/Person Check",
      description:
        "Get guidance on evaluating business or personal interactions",
    },
    {
      id: 7,
      question:
        "... analyze a <strong>text conversation</strong> for scams or threats?",
      subtitle:
        "Paste a text conversation thread to detect manipulation, fraud, or suspicious patterns.",
      icon: faComments,
      action: "Text Conversation",
      description:
        "Analyze entire text conversation threads for scam patterns and manipulation tactics",
    },
    {
      id: 8,
      question: "... pause <strong>simulations</strong>?",
      subtitle: "Temporarily stop receiving phishing simulation exercises",
      icon: faPause,
      action: "Simulation Profile",
      description: "Pause or resume your simulation training schedule",
    },
    {
      id: 9,
      question: "... access <strong>security training</strong> and lessons?",
      subtitle:
        "Learn how to protect yourself and your family from online threats",
      icon: faGraduationCap,
      action: "Training Center",
      description: "Access security training courses, lessons, and quizzes",
      showInPopular: false,
    },
    {
      id: 10,
      question: "... view the <strong>Alerts Intelligence Hub</strong>?",
      subtitle:
        "Browse and explore scam alerts by category, threat level, and recent trends",
      icon: faBell,
      action: "Alerts Intelligence Hub",
      description:
        "Access the Alerts Intelligence Hub to browse scam alerts and threat intelligence",
      showInPopular: false,
    },
    {
      id: 11,
      question: "... manage my <strong>account and billing</strong>?",
      subtitle:
        "View and update your account information, billing details, and invoices",
      icon: faFileInvoiceDollar,
      action: "Account & Billing",
      description:
        "Manage your account information, billing, invoices, and payment history",
      showInPopular: false,
    },
    {
      id: 12,
      question: "... view or change my <strong>subscription</strong>?",
      subtitle:
        "View your current plan, upgrade, downgrade, or cancel your subscription",
      icon: faClipboardList,
      action: "Subscriptions",
      description:
        "Manage your subscription plan, upgrade, downgrade, or cancel",
      showInPopular: false,
    },
    {
      id: 13,
      question: "... update my <strong>payment method</strong> or credit card?",
      subtitle: "Add, update, or remove a credit card or payment method",
      icon: faCreditCard,
      action: "Payments:Methods",
      description:
        "Add, update, or remove credit cards and payment methods on your account",
      showInPopular: false,
    },
    {
      id: 14,
      question: "... view my <strong>payment history</strong>?",
      subtitle: "View past payments, invoices, and transaction details",
      icon: faFileInvoiceDollar,
      action: "Payments:History",
      description:
        "View payment history, past invoices, and transaction records",
      showInPopular: false,
    },
    {
      id: 15,
      question: "... manage <strong>users</strong> on my account?",
      subtitle: "View, add, or manage users and family members on your account",
      icon: faUsers,
      action: "Member User Info",
      description:
        "View user list, manage family members, and update user details",
      showInPopular: false,
    },
    {
      id: 16,
      question: "... <strong>create a new user</strong> on my account?",
      subtitle: "Add a new family member or user to your KinShield account",
      icon: faUserPlus,
      action: "Member User Info:Create",
      description: "Create and add a new user or family member to your account",
      showInPopular: false,
    },
    {
      id: 17,
      question: "... change my <strong>password</strong> or security settings?",
      subtitle:
        "Update your password, security questions, or authentication settings",
      icon: faKey,
      action: "Settings:ChangePassword",
      description:
        "Change your password, update security settings, and manage authentication",
      showInPopular: false,
    },
    {
      id: 18,
      question: "... update my <strong>preferences and settings</strong>?",
      subtitle:
        "Configure notification preferences, display settings, and account options",
      icon: faCog,
      action: "Settings:Notifications",
      description:
        "Update your notification preferences, display settings, and account configuration",
      showInPopular: false,
    },
    {
      id: 19,
      question: "... view my <strong>notifications</strong>?",
      subtitle: "View alerts, updates, and messages from KinShield",
      icon: faBell,
      action: "Notifications",
      description:
        "View and manage your notification inbox, alerts, and system messages",
      showInPopular: false,
    },
    {
      id: 20,
      question: "... view my <strong>user profile</strong> details?",
      subtitle:
        "View and update your personal information, email, and profile details",
      icon: faIdCard,
      action: "Member User Info:Details",
      description:
        "View and edit your personal profile information and account details",
      showInPopular: false,
    },
    {
      id: 21,
      question: "... view available <strong>service plans</strong>?",
      subtitle: "Compare plans, features, and pricing to find the right fit",
      icon: faClipboardList,
      action: "Service Plans",
      description:
        "Browse and compare available service plans, features, and pricing",
      showInPopular: false,
    },
    {
      id: 22,
      question: "... add a <strong>new credit card</strong>?",
      subtitle: "Add a new credit or debit card for payments",
      icon: faCreditCard,
      action: "Payments:AddCard",
      description: "Add a new credit or debit card to your account",
      showInPopular: false,
    },
    {
      id: 23,
      question: "... resend a <strong>verification key</strong> for a user?",
      subtitle:
        "Resend activate or verification emails for users in your group",
      icon: faKey,
      action: "Member User Info:ResendVerification",
      description:
        "Resend verification or activation keys to users in your account",
      showInPopular: false,
    },
    {
      id: 24,
      question:
        "... view <strong>Shield Reports</strong> and security summaries?",
      subtitle:
        "Access detailed security reports and threat analysis summaries",
      icon: faUserCog,
      action: "Shield Reports",
      description:
        "View Shield Reports, security summaries, and threat analysis results",
      showInPopular: false,
    },
    {
      id: 25,
      question:
        "... change my <strong>phone number</strong> or <strong>email</strong>?",
      subtitle:
        "Update the phone number or email address associated with your account",
      icon: faIdCard,
      action: "Member User Info:Update",
      description:
        "Change or update your phone number or email address in your user profile",
      showInPopular: false,
    },
    {
      id: 26,
      question: "... check if a <strong>URL or link</strong> is safe?",
      subtitle:
        "Paste a suspicious link to check for phishing, malware, or fraudulent websites",
      icon: faLink,
      action: "Link Safety",
      description:
        "Analyze URLs and links for phishing indicators and safety risks",
      showInPopular: false,
    },
    {
      id: 27,
      question: "... view my <strong>safety score</strong>?",
      subtitle:
        "See your personal security score based on training, simulations, and engagement",
      icon: faShieldAlt,
      action: "Dashboard",
      description:
        "View your multi-criteria safety score and how it's calculated",
      showInPopular: false,
    },
    {
      id: 28,
      question: "... set up <strong>two-factor authentication</strong> (2FA)?",
      subtitle:
        "Enable multi-factor authentication with SMS verification or backup codes",
      icon: faLock,
      action: "Settings:MFA",
      description:
        "Set up 2FA/MFA to add an extra layer of security to your account",
      showInPopular: false,
    },
    {
      id: 43,
      question: "... <strong>disable two-factor authentication</strong> (2FA)?",
      subtitle: "Turn off multi-factor authentication on your account",
      icon: faLock,
      action: "Settings:DisableMFA",
      description:
        "Disable 2FA/MFA if you no longer want the extra authentication step",
      showInPopular: false,
    },
    {
      id: 29,
      question: "... <strong>resume</strong> simulations?",
      subtitle:
        "Re-enable paused phishing simulation exercises to continue training",
      icon: faPlay,
      action: "Simulation Profile",
      description: "Resume previously paused phishing simulation exercises",
      showInPopular: false,
    },
    {
      id: 30,
      question: "... <strong>share an alert</strong> with a family member?",
      subtitle:
        "Send a scam alert to a family member on your KinShield account",
      icon: faShareSquare,
      action: "Alerts Intelligence Hub",
      description:
        "Share scam alerts and threat intelligence with family members",
      showInPopular: false,
    },
    {
      id: 31,
      question:
        "... view my training <strong>badges and certificates</strong>?",
      subtitle:
        "See achievements earned from completing security training courses",
      icon: faMedal,
      action: "Training Center",
      description:
        "View earned training badges, certificates, and achievement history",
      showInPopular: false,
    },
    {
      id: 32,
      question: "... take a <strong>security quiz</strong>?",
      subtitle: "Test your cybersecurity knowledge with interactive quizzes",
      icon: faChalkboardTeacher,
      action: "Training Center",
      description:
        "Take security quizzes to test and improve your cybersecurity knowledge",
      showInPopular: false,
    },
    {
      id: 33,
      question: "... <strong>reset my password</strong>?",
      subtitle:
        "Forgot your password or need to change it for security reasons",
      icon: faUndo,
      action: "Settings:ChangePassword",
      description: "Reset or change your account password",
      showInPopular: false,
    },
    {
      id: 34,
      question: "... check my <strong>risk assessment</strong>?",
      subtitle:
        "View your risk level based on simulation interactions and security behavior",
      icon: faChartLine,
      action: "Dashboard",
      description: "View your risk assessment score and vulnerability analysis",
      showInPopular: false,
    },
    {
      id: 35,
      question: "... apply a <strong>promo code</strong> or discount?",
      subtitle: "Enter a promotion code to get a discount on your subscription",
      icon: faTag,
      action: "Subscriptions",
      description:
        "Apply a promotion code or discount to your subscription plan",
      showInPopular: false,
    },
    {
      id: 36,
      question: "... manage <strong>SMS consent</strong>?",
      subtitle: "Opt in or out of receiving SMS messages from KinShield",
      icon: faMobileAlt,
      action: "Settings:SmsConsent",
      description:
        "Manage your SMS communication preferences and consent settings",
      showInPopular: false,
    },
    {
      id: 37,
      question: "... <strong>contact support</strong>?",
      subtitle:
        "Submit a question, report an issue, or get help from the KinShield team",
      icon: faHeadset,
      action: "ContactSupport",
      description: "Contact the KinShield support team via the contact form",
      showInPopular: false,
    },
    {
      id: 38,
      question:
        "... <strong>download</strong> my security report as a <strong>PDF</strong>?",
      subtitle: "Generate and download a PDF version of your Shield Report",
      icon: faFilePdf,
      action: "Shield Reports",
      description: "Download your security report as a PDF document",
      showInPopular: false,
    },
    {
      id: 39,
      question: "... view <strong>blog posts</strong> and security articles?",
      subtitle: "Read cybersecurity tips, news, and educational articles",
      icon: faNewspaper,
      action: "Dashboard",
      description:
        "Browse blog posts and security articles for cybersecurity education",
      showInPopular: false,
    },
    {
      id: 40,
      question: "... view my <strong>simulation history</strong>?",
      subtitle: "See past phishing simulations and how you responded to them",
      icon: faHistory,
      action: "Recent Simulations",
      description:
        "View history of past phishing simulations and your interaction results",
      showInPopular: false,
    },
    {
      id: 41,
      question: "... <strong>cancel</strong> my subscription?",
      subtitle: "Cancel your current plan and understand what happens next",
      icon: faTimesCircle,
      action: "Subscriptions",
      description:
        "Cancel your subscription plan and learn about the cancellation process",
      showInPopular: false,
    },
    {
      id: 42,
      question: "... <strong>upgrade or downgrade</strong> my plan?",
      subtitle: "Switch between service tiers to match your needs",
      icon: faArrowsAltV,
      action: "Subscriptions",
      description:
        "Upgrade or downgrade your subscription to a different service tier",
      showInPopular: false,
    },
  ];

  // Determine which questions to display based on AI search results or defaults
  const filteredQuestions = helpQuestions.filter((item) => {
    // If AI search returned matched IDs, show only those
    if (aiMatchedIds !== null) {
      return aiMatchedIds.includes(item.id);
    }
    // Default: show only items marked for Popular Questions
    return item.showInPopular !== false;
  });

  // Sort AI results by the order returned (most relevant first)
  if (aiMatchedIds !== null) {
    filteredQuestions.sort(
      (a, b) => aiMatchedIds.indexOf(a.id) - aiMatchedIds.indexOf(b.id),
    );
  }

  const handleQuestionClick = (action, id) => {
    console.log("HowDoI: Navigating to:", action);
    if (id) saveClickedCard(id);
    setHeroContent(action);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearch = useCallback(async () => {
    const query = searchQuery.trim();
    if (!query) {
      // Empty search — reset to popular questions
      setActiveSearchQuery("");
      setAiMatchedIds(null);
      setSearchError(false);
      return;
    }

    setActiveSearchQuery(query);
    setSearchError(false);
    setShowSuggestions(false);
    saveSearch(query);

    // Check cache first
    const cached = searchCacheRef.current.get(query.toLowerCase());
    if (cached) {
      setAiMatchedIds(cached);
      return;
    }

    setIsSearching(true);
    try {
      const result = await HowDoISearchAPI.search(query);
      const ids = result?.matchedIds || [];
      // Cache the result
      searchCacheRef.current.set(query.toLowerCase(), ids);
      setAiMatchedIds(ids);
    } catch (error) {
      console.error("AI search failed, showing all options:", error);
      setSearchError(true);
      // On failure, show all options so the user isn't stuck with no results
      setAiMatchedIds(null);
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery]);

  const clearSearch = () => {
    setSearchQuery("");
    setActiveSearchQuery("");
    setAiMatchedIds(null);
    setSearchError(false);
  };

  return (
    <div className="how-do-i-container">
      <div className="how-do-i-header">
        <div className="header-title-section">
          <h1>How do I ...?</h1>
          <p className="subtitle">
            The easy way to find the answer to your question
          </p>
        </div>
        <div className="header-actions">
          <div className="howdoi-search-container">
            <div className="howdoi-search-input-wrapper" ref={inputWrapperRef}>
              <input
                type="text"
                placeholder="Search on what to do..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="howdoi-search-input"
                onFocus={() => setShowSuggestions(true)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              />
              {searchQuery && (
                <button
                  className="howdoi-search-clear-btn"
                  onClick={clearSearch}
                  title="Clear search"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
              {showSuggestions && !searchQuery && (
                <div
                  className="howdoi-suggestions-dropdown"
                  ref={suggestionsRef}
                >
                  <div className="howdoi-suggestions-header">
                    {getRecentSearches().length > 0
                      ? "Recent Searches"
                      : "Suggested Searches"}
                  </div>
                  {getSuggestions().map((suggestion, idx) => (
                    <button
                      key={idx}
                      className="howdoi-suggestion-item"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setSearchQuery(suggestion.label);
                        setShowSuggestions(false);
                        // Trigger search with this query
                        setActiveSearchQuery(suggestion.label);
                        saveSearch(suggestion.label);
                        const cached = searchCacheRef.current.get(
                          suggestion.label.toLowerCase(),
                        );
                        if (cached) {
                          setAiMatchedIds(cached);
                        } else {
                          // Set query and trigger search
                          setIsSearching(true);
                          HowDoISearchAPI.search(suggestion.label)
                            .then((result) => {
                              const ids = result?.matchedIds || [];
                              searchCacheRef.current.set(
                                suggestion.label.toLowerCase(),
                                ids,
                              );
                              setAiMatchedIds(ids);
                            })
                            .catch((error) => {
                              console.error("AI search failed:", error);
                              setSearchError(true);
                              setAiMatchedIds(null);
                            })
                            .finally(() => setIsSearching(false));
                        }
                      }}
                    >
                      <span className="howdoi-suggestion-icon">
                        {suggestion.type === "recent" ? "🕐" : "🔍"}
                      </span>
                      <span className="howdoi-suggestion-text">
                        {suggestion.label}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              className="howdoi-search-submit-btn"
              onClick={handleSearch}
              title="Search"
            >
              Search
            </button>
          </div>
        </div>
      </div>
      {/* Pin as default landing page */}
      <div className="howdoi-pin-default-row">
        <button
          className={`pin-default-link${isDefaultPage ? " is-default" : ""}${pinConfirmed ? " confirmed" : ""}`}
          onClick={handlePinAsDefault}
          disabled={isDefaultPage}
          title={
            isDefaultPage
              ? "This is already your default page"
              : "Set this page as your default landing page after login"
          }
        >
          <FontAwesomeIcon icon={isDefaultPage ? faCheckCircle : faThumbtack} />
        </button>
      </div>

      {/* Questions List */}
      <div className="questions-section">
        <h3>{activeSearchQuery ? "Search Results" : "Popular Questions"}</h3>
        {isSearching && (
          <div className="howdoi-search-loading">
            <div className="howdoi-spinner" />
            <p>Finding the best match for your question...</p>
          </div>
        )}
        {searchError && (
          <div className="howdoi-search-error">
            <p>Search is temporarily unavailable. Showing all options.</p>
          </div>
        )}
        <div className="questions-grid">
          {!isSearching && filteredQuestions.length > 0
            ? filteredQuestions.map((item) => (
                <div
                  key={item.id}
                  className="question-card"
                  onClick={() => handleQuestionClick(item.action, item.id)}
                >
                  <div className="question-icon">
                    <FontAwesomeIcon icon={item.icon} />
                  </div>
                  <div className="question-content">
                    <h4
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(item.question, {
                          ALLOWED_TAGS: ["strong", "em", "u", "br"],
                          ALLOWED_ATTR: [],
                        }),
                      }}
                    />
                    {item.subtitle && item.subtitle.trim() && (
                      <h5
                        className="question-subtitle"
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(item.subtitle, {
                            ALLOWED_TAGS: ["strong", "em", "u", "br"],
                            ALLOWED_ATTR: [],
                          }),
                        }}
                      />
                    )}
                  </div>
                  <div className="question-arrow">→</div>
                </div>
              ))
            : !isSearching && (
                <div className="no-results">
                  <FontAwesomeIcon icon={faQuestionCircle} />
                  <p>No questions found matching your search.</p>
                  <p>
                    Try rephrasing your question or browse all options by
                    clearing the search.
                  </p>
                </div>
              )}
        </div>
      </div>

      {/* Additional Help Section */}
      <div className="additional-help">
        <h3>Need More Help?</h3>
        <p className="additional-help-chatbot-tip">
          <FontAwesomeIcon icon={faComments} className="chatbot-tip-icon" />
          You can also ask <strong>Scout</strong>, our{" "}
          <strong>Digital Personal Guard</strong> chatbot, for help anytime —
          just click the chat bubble in the bottom-right corner of your screen.
        </p>
        <div className="help-options">
          <button
            className="help-option-btn"
            onClick={() => setHeroContent("ContactSupport")}
          >
            <FontAwesomeIcon icon={faQuestionCircle} />
            Contact Support
          </button>
        </div>
      </div>

      {/* Back to Setup Journey link — shown when navigated from AccountSetupJourney */}
      {fromSetupJourney && setHeroContent && (
        <div className="setup-journey-return">
          <button
            className="setup-journey-return-link"
            onClick={() => {
              sessionStorage.setItem("expandSetupJourney", "true");
              setHeroContent("Dashboard");
            }}
          >
            <span className="return-arrow">←</span>
            Continue Setting Up Your Account
          </button>
        </div>
      )}
    </div>
  );
}

export default HowDoI;
